'use client';

import {
  type ReactNode,
  createContext,
  useContext,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from 'react';

import { lens, persistOptions, withLenses } from '@dhmk/zustand-lens';
import { type StoreApi } from 'zustand';
import { createStore } from 'zustand';
import { createJSONStorage, persist } from 'zustand/middleware';
import { shallow } from 'zustand/shallow';
import { useStoreWithEqualityFn } from 'zustand/traditional';

import { helpHubStoreCookieName, setCookie } from '../utils/cookies';
import { type CatalogSlice, CatalogStore } from './catalog-slice';
import { type ChatbotSlice, ChatbotStore } from './chatbot-slice';
import { resolveDraftIssueSelections } from './draft-issue-selection';
import { type DraftIssueSlice, DraftIssueStore } from './draft-issue-slice';
import { type LocationSlice, LocationStore } from './location-slice';

export const STORE_NAME = 'help-hub';

type ExtractState<S> = S extends { getState: () => infer T } ? T : never;

export type Store = {
  catalog: CatalogSlice;
  chatbot: ChatbotSlice;
  draftIssue: DraftIssueSlice;
  location: LocationSlice;
};

export type InitialState = {
  catalog?: Partial<CatalogSlice>;
  chatbot?: Partial<ChatbotSlice>;
  draftIssue?: Partial<DraftIssueSlice>;
  location?: Partial<LocationSlice>;
};

const initialiseStore = (initialState?: InitialState) => {
  const catalogSlice = new CatalogStore();
  const catalog = lens(
    catalogSlice.createSlice({
      ...initialState?.catalog,
    })
  );

  const chatbotSlice = new ChatbotStore();
  const chatbot = lens(
    chatbotSlice.createSlice({
      ...initialState?.chatbot,
    })
  );

  const draftIssueSlice = new DraftIssueStore();
  const draftIssue = lens(
    draftIssueSlice.createSlice({
      ...initialState?.draftIssue,
    })
  );

  const locationSlice = new LocationStore();
  const location = lens(
    locationSlice.createSlice({
      ...initialState?.location,
    })
  );

  const store = createStore(
    persist<Store>(
      withLenses({
        catalog,
        chatbot,
        draftIssue,
        location,
      }),
      {
        name: STORE_NAME,
        skipHydration: true,
        storage: createJSONStorage(() => {
          return localStorage;
        }),
        ...persistOptions,
        partialize: (state) => {
          return {
            ...state,
            catalog: catalogSlice.partialize(state) as CatalogSlice,
            chatbot: chatbotSlice.partialize(state) as ChatbotSlice,
            draftIssue: draftIssueSlice.partialize(state),
            location: locationSlice.partialize(state),
          };
        },
      }
    )
  );

  store.subscribe(({ location }) => {
    if (location.store?.id) {
      setCookie(helpHubStoreCookieName, location.store.id);
    }
  });

  return store;
};

type HelpHubStore = ReturnType<typeof initialiseStore>;

const StoreContext = createContext<HelpHubStore>(initialiseStore());

export const StoreProvider = ({
  children,
  initialState,
}: {
  children: ReactNode;
  initialState?: Partial<InitialState>;
}) => {
  const store = useRef(initialiseStore(initialState)).current;

  useLayoutEffect(() => {
    void store.persist.rehydrate();
  }, [store]);

  return (
    <StoreContext.Provider value={store}>{children}</StoreContext.Provider>
  );
};

export const useStore = <U,>(
  selector: (state: ExtractState<StoreApi<Store>>) => U
): U => useStoreWithEqualityFn(useContext(StoreContext), selector, shallow);

export const useHasHydrated = () => {
  const store = useContext(StoreContext);
  const [hasHydrated, setHydrated] = useState(
    store.persist?.hasHydrated() ?? false
  );

  useEffect(() => {
    if (!store.persist) {
      return;
    }
    const unsubscribe = store.persist.onFinishHydration(() =>
      setHydrated(true)
    );
    // Hydration may have completed before the listener was registered
    if (store.persist.hasHydrated()) {
      setHydrated(true);
    }
    return unsubscribe;
  }, [store]);

  return hasHydrated;
};

export const selectDraftIssueWithStore = ({
  catalog,
  draftIssue,
  location,
}: {
  catalog: CatalogSlice;
  draftIssue: DraftIssueSlice;
  location: LocationSlice;
}) => {
  const resolvedSelections = resolveDraftIssueSelections(
    draftIssue,
    catalog.catalog?.categories ?? []
  );

  return {
    ...draftIssue,
    ...resolvedSelections,
    store: location.store,
  };
};
